import type { BrandDna } from "pages/DesignStudioPage/types/brandDna";
import {
  IMiroExportResult,
  IMiroMoodboardImage,
  IMiroMoodboardResult,
  IMiroMoodboardJobRequest,
  MIRO_MAX_MOODBOARD_COLORS,
  MIRO_MAX_MOODBOARD_FABRICS,
  MIRO_MAX_MOODBOARD_IMAGES,
  MIRO_MAX_MOODBOARD_PRINTS,
  MIRO_MAX_REPORT_ID,
} from "api/miro/miro.type";

export interface MiroMoodboardPlan {
  body: IMiroMoodboardJobRequest;
  /** What will actually be sent, after the BE's per-section caps. */
  counts: { images: number; prints: number; fabrics: number; colors: number };
  /** Something was cut to fit a cap — the dialog says so rather than dropping it silently. */
  trimmed: boolean;
}

const unique = (urls: Array<string | null | undefined>): string[] => {
  const seen = new Set<string>();

  return urls.filter((url): url is string => {
    if (typeof url !== "string" || url.trim() === "" || seen.has(url)) return false;
    seen.add(url);

    return true;
  });
};

const named = (items: Array<{ name: string; thumbnail?: string }> | undefined): IMiroMoodboardImage[] =>
  (items ?? []).filter((item) => typeof item.thumbnail === "string" && item.thumbnail !== "").map((item) => ({ url: item.thumbnail as string, ...(item.name ? { title: item.name } : {}) }));

/**
 * Maps a moodboard (`BrandDna`) onto the BE's `moodboard-jobs` body: images ← the analysed looks (full-resolution
 * `imageDataItems[].url`, never `resized_image`) then the cover images; prints ← patterns; fabrics ← fabrics;
 * colours ← `topColors` with name and code. A section is cut to its cap in order, so the leading items win.
 */
/** The BE logs the moodboard a block export came from: `report_id` is 1–64 characters, so anything else is left out rather than refused. */
const reportIdOf = (moodboard: BrandDna): { report_id: string } | Record<string, never> => {
  const id = typeof moodboard.id === "string" || typeof moodboard.id === "number" ? String(moodboard.id).trim() : "";

  return id.length >= 1 && id.length <= MIRO_MAX_REPORT_ID ? { report_id: id } : {};
};

export const buildMoodboardJob = (moodboard: BrandDna, fallbackTitle: string): MiroMoodboardPlan => {
  const allImages = unique([...(moodboard.imageDataItems ?? []).map((item) => item.url), ...(moodboard.coverImages ?? [])]);
  const allPrints = named(moodboard.patterns);
  const allFabrics = named(moodboard.fabrics);
  const allColors = (moodboard.topColors ?? []).filter((c) => typeof c.hex === "string" && c.hex !== "");

  const images = allImages.slice(0, MIRO_MAX_MOODBOARD_IMAGES).map((url) => ({ url }));
  const prints = allPrints.slice(0, MIRO_MAX_MOODBOARD_PRINTS);
  const fabrics = allFabrics.slice(0, MIRO_MAX_MOODBOARD_FABRICS);
  const colors = allColors.slice(0, MIRO_MAX_MOODBOARD_COLORS).map((c) => ({ hex: c.hex, ...(c.name ? { name: c.name } : {}), ...(c.code ? { code: c.code } : {}) }));

  return {
    body: { title: moodboard.title?.trim() || fallbackTitle, ...reportIdOf(moodboard), images, prints, fabrics, colors },
    counts: { images: images.length, prints: prints.length, fabrics: fabrics.length, colors: colors.length },
    trimmed: allImages.length > images.length || allPrints.length > prints.length || allFabrics.length > fabrics.length || allColors.length > colors.length,
  };
};

/**
 * The results a user can recognise: the images, prints, fabrics and colours they sent. The title and the section
 * labels the BE adds for layout are skipped, so "3 of 12 placed" counts the user's own items — unless that would
 * leave nothing (a job that only got as far as the title), then everything is shown.
 */
export const moodboardUserResults = <T extends IMiroExportResult | IMiroMoodboardResult>(results: T[]): T[] => {
  const mine = results.filter((r) => (r as IMiroMoodboardResult).kind !== "title" && (r as IMiroMoodboardResult).kind !== "label");

  return mine.length > 0 ? mine : results;
};
